import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { SpinnerIcon } from "@/shared/components/icons";
import { cn } from "@/shared/lib/cn";

type ButtonVariant = "primary" | "secondary" | "neutral" | "danger";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  isLoading?: boolean;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      icon,
      isLoading = false,
      fullWidth = false,
      disabled,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading;

    return (
      <button
        ref={ref}
        className={cn(
          // Base
          "inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg font-semibold",
          "transition-[color,background-color,border-color,box-shadow]",
          "focus-visible:ring-brand-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Size
          size === "sm" && "control-sm",
          size === "md" && "control-md",
          size === "lg" && "control-lg",
          // Variants
          variant === "primary" && [
            "bg-brand-primary text-white shadow-sm",
            "hover:bg-brand-secondary active:bg-brand-secondary",
          ],
          variant === "secondary" && [
            "border-brand-primary text-brand-primary border-2 bg-white",
            "hover:bg-brand-primary hover:text-white",
            "disabled:hover:text-brand-primary disabled:hover:bg-white",
          ],
          variant === "neutral" && [
            "border-2 border-gray-300 bg-white text-gray-700",
            "hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900",
            "disabled:hover:border-gray-300 disabled:hover:bg-white disabled:hover:text-gray-700",
          ],
          variant === "danger" && [
            "border-2 border-red-300 bg-white text-red-600",
            "hover:border-red-400 hover:bg-red-50",
            "disabled:hover:border-red-300 disabled:hover:bg-white",
          ],
          fullWidth && "w-full",
          className
        )}
        disabled={isDisabled}
        type={type}
        {...props}
      >
        {/* Wrapper — relative context for the spinner, invisible during loading to preserve size */}
        <span className="relative inline-flex items-center gap-2">
          {/* Spinner — centered absolutely, only visible during loading */}
          {isLoading && (
            <span className="absolute inset-0 flex items-center justify-center">
              <SpinnerIcon
                aria-hidden="true"
                className={cn(
                  "animate-spin",
                  size === "sm" && "h-4 w-4",
                  size === "md" && "h-5 w-5",
                  size === "lg" && "h-6 w-6"
                )}
              />
            </span>
          )}

          {/* Content — invisible during loading to preserve button size */}
          <span
            aria-hidden={isLoading}
            className={cn("inline-flex items-center gap-2", isLoading && "invisible")}
          >
            {icon && <span className="h-5 w-5 [&>svg]:h-full [&>svg]:w-full">{icon}</span>}
            {children}
          </span>
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";
